import { GameVars } from "./../../../GameVars";
import { GameConstants } from "../../../GameConstants";
import { BoardContainer } from "../BoardContainer";
import { GUI } from "./GUI";
import { TutorialScene } from "../../instructions-scene/TutorialScene";

export class StrikerSlider extends Phaser.GameObjects.Container {

    public static currentInstance: StrikerSlider;

    public tutorialCursor: Phaser.GameObjects.Image;

    private cursor: Phaser.GameObjects.Image;
    private isDown: boolean;
    private activated: boolean;
    private guide: Phaser.GameObjects.Image;
    private gui: GUI;

    public get cursorPosition(): number {
        
        return this.cursor.x / GameConstants.PHYS_SCALE;
    }

    public set cursorPosition(x: number) {

        this.cursor.x = x / GameConstants.PHYS_SCALE;
    }

    constructor(scene: Phaser.Scene, gui: GUI) {
        
        super(scene);

        StrikerSlider.currentInstance = this;

        this.gui = gui;

        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = GameConstants.GAME_HEIGHT / 2 + 450 * GameVars.scaleY * BoardContainer.currentInstance.scaleX;
        this.scaleY = GameVars.scaleY;

        this.isDown = false;

        this.guide = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "slide_striker");
        this.add(this.guide);

        this.cursor = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "striker_" + GameVars.gameData.strikerId);
        this.cursor.setInteractive();
        this.cursor.setScale(.5);
        
        this.cursor.on("pointerdown", this.onCursorDown, this);
        this.cursor.on("pointerup", this.onCursorUp, this);
    
        this.add(this.cursor);

        if (GameVars.tutorialMode) {
            this.tutorialCursor = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "striker_" + GameVars.gameData.strikerId);
            this.tutorialCursor.setScale(.5);
            this.tutorialCursor.alpha = .35;
            this.add(this.tutorialCursor);
        }
    }

    public update(): void {

        if (GameVars.tutorialMode && GameVars.tutorialStage === TutorialStage.STAGE_1 && this.gui.tutorialCursor) {
            this.tutorialCursor.x = this.gui.tutorialCursor.x - GameConstants.GAME_WIDTH / 2;
        }

        if (!this.activated) {
            return;
        }

        const strikerSliderWidth = GameVars.carromEngine.strikerSliderWidth;

        if (this.isDown && this.scene.input.activePointer.isDown) {

            const x = this.scene.input.activePointer.x - this.x;
            const minX  = -strikerSliderWidth * .5 * GameConstants.PHYS_SCALE;
            const maxX = strikerSliderWidth * .5 * GameConstants.PHYS_SCALE;

            this.cursor.x = Phaser.Math.Clamp(x, minX, maxX);
        }  

        if (!this.scene.input.activePointer.isDown) {

            if (this.isDown) {
                this.onCursorUp();
            }
        }
    }

    public activate(): void {

        this.activated = true;
        this.alpha = 1;
        this.cursor.x = 0;

        const isColliding = this.doesStrikerCollidesWithPucks(this.cursor.x);

        if (isColliding) {
            this.handleCollisionsWithPucks();  
        }
    }

    public deactivate(): void {

        this.activated = false;
        this.alpha = .5;
    }

    public resetCursorPosition(): void {

        this.cursor.x = 0;
        this.isDown = false;
    }

    public doesStrikerCollidesWithPucks(strikerX: number): boolean {

        let collides = false;

        strikerX /= GameConstants.PHYS_SCALE;

        let strikerY = GameVars.carromEngine.strikerY;

        if (GameVars.reversedBoard) {
            strikerX *= -1;
            strikerY *= -1;
        }

        for (let i = 0; i < GameVars.pucks.length; i ++) {

            const pX = GameVars.pucks[i].position.x;
            const pY = GameVars.pucks[i].position.y;

            const d = Phaser.Math.Distance.Between(strikerX, strikerY, pX, pY);
            const puckRadius = GameVars.carromEngine.puckRadius;
            const strikerRadius = GameVars.carromEngine.strikerRadius;

            if (d < strikerRadius + puckRadius) {
                collides = true;
                break;
            }
        }

        return collides;
    }

    private onCursorDown(): void {

        if ((!GameVars.connection && !GameVars.tutorialMode) || !this.activated || GameVars.paused) {
            return;
        }

        this.isDown = true;

        if (GameVars.tutorialMode && GameVars.tutorialStage === TutorialStage.STAGE_1) {
            const tutorialManager = TutorialScene.currentInstance.tutorialManager;
            tutorialManager.onSliderDown();
        }

        this.guide.tint = 0xCCCCCC;
    }

    private onCursorUp(): void {

        if ((!GameVars.connection && !GameVars.tutorialMode) || !this.activated || GameVars.paused) {
            return;
        }

        if (!this.activated || GameVars.paused) {
            return;
        }

        this.isDown = false;

        this.guide.tint = 0xFFFFFF;

        const isColliding = this.doesStrikerCollidesWithPucks(this.cursor.x);

        if (isColliding) {
            this.handleCollisionsWithPucks();  
        }  

        if (GameVars.tutorialMode && GameVars.tutorialStage === TutorialStage.STAGE_2) {
            const tutorialManager = TutorialScene.currentInstance.tutorialManager;
            tutorialManager.onSliderUp();
        }
    }
    
    private handleCollisionsWithPucks(): void {

        let pLeft = this.cursor.x;
        let pRight = this.cursor.x;

        const strikerRadius = GameVars.carromEngine.strikerRadius;

        const dx = strikerRadius * GameConstants.PHYS_SCALE / 50;
        
        let sanityCheck = 0;
        
        do {
            pLeft -= dx;
            sanityCheck ++;

        } while (this.doesStrikerCollidesWithPucks(pLeft) && sanityCheck < 1000);

        sanityCheck = 0;

        do {
            pRight += dx;
            sanityCheck ++;

        } while (this.doesStrikerCollidesWithPucks(pRight) && sanityCheck < 1000);

        let px: number;
        const strikerSliderWidth = GameVars.carromEngine.strikerSliderWidth;

        if (pRight > strikerSliderWidth * .5 * GameConstants.PHYS_SCALE) {
            px = pLeft;
        } else if (pLeft < -strikerSliderWidth * .5 * GameConstants.PHYS_SCALE) {
            px = pRight;
        } else {
            px = this.cursor.x - pLeft < pRight - this.cursor.x ? pLeft : pRight;
        }

        const t = Math.round(Math.abs(this.cursor.x - px) * 7.5);

        this.scene.add.tween({
            targets: this.cursor,
            x: px,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: t,
            onComplete: function(): void {
                
                this.tweening = false;
            },
            onCompleteScope: this
        });
    }
}
